import { seeded } from "./motion";

// A human brain in profile, facing left, drawn like an engraving. Units are the brain's radius.
const PROFILE = [
  [-1.13, 0.16],
  [-1.22, -0.16],
  [-1.1, -0.56],
  [-0.78, -0.85],
  [-0.3, -1.0],
  [0.22, -1.0],
  [0.68, -0.87],
  [1.03, -0.6],
  [1.22, -0.24],
  [1.2, 0.12],
  [1.04, 0.33],
  [0.7, 0.4],
  [0.3, 0.47],
  [-0.1, 0.53],
  [-0.44, 0.5],
  [-0.62, 0.36],
  [-0.58, 0.22],
  [-0.8, 0.27],
  [-1.0, 0.27],
];
const CEREBELLUM = { x: 0.78, y: 0.55, rx: 0.37, ry: 0.21, rot: -0.14 };

// Closed Catmull-Rom curve through the profile points.
function smooth(points, per) {
  const out = [];
  const n = points.length;
  for (let i = 0; i < n; i += 1) {
    const [p0, p1, p2, p3] = [points[(i - 1 + n) % n], points[i], points[(i + 1) % n], points[(i + 2) % n]];
    for (let k = 0; k < per; k += 1) {
      const t = k / per;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push([0, 1].map((d) =>
        0.5 * (2 * p1[d] + (-p0[d] + p2[d]) * t + (2 * p0[d] - 5 * p1[d] + 4 * p2[d] - p3[d]) * t2 + (-p0[d] + 3 * p1[d] - 3 * p2[d] + p3[d]) * t3),
      ));
    }
  }
  return out;
}
const CEREBRUM = smooth(PROFILE, 14);

function inPolygon(poly, x, y) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
const inCerebrum = (x, y) => inPolygon(CEREBRUM, x, y);
function inCerebellum(x, y) {
  const { x: cx, y: cy, rx, ry, rot } = CEREBELLUM;
  const dx = x - cx;
  const dy = y - cy;
  const u = dx * Math.cos(-rot) - dy * Math.sin(-rot);
  const v = dx * Math.sin(-rot) + dy * Math.cos(-rot);
  return (u / rx) ** 2 + (v / ry) ** 2 < 1;
}
export const inBrain = (x, y) => inCerebrum(x, y) || inCerebellum(x, y);

// The folds of the cortex: the zero contours of a field made of many interfering waves. Traced
// with marching squares, they meander and branch like real gyri (and like brain coral).
export function buildGyri() {
  const rand = seeded(77);
  const waves = Array.from({ length: 16 }, () => {
    const a = rand() * Math.PI * 2;
    const k = 28 + rand() * 6;
    return [Math.cos(a) * k, Math.sin(a) * k, rand() * Math.PI * 2];
  });
  const field = (x, y) => waves.reduce((sum, [kx, ky, ph]) => sum + Math.sin(kx * x + ky * y + ph), 0);
  const cell = 0.0075;
  const x0 = -1.26;
  const y0 = -1.04;
  const cols = Math.ceil(2.52 / cell);
  const rows = Math.ceil(1.62 / cell);
  const values = new Float32Array((cols + 1) * (rows + 1));
  for (let j = 0; j <= rows; j += 1)
    for (let i = 0; i <= cols; i += 1) values[j * (cols + 1) + i] = field(x0 + i * cell, y0 + j * cell);
  const segs = [];
  const edge = (i, j, e) => {
    // Where the contour crosses edge e of cell (i, j): 0 top, 1 right, 2 bottom, 3 left
    const v = (a, b) => values[b * (cols + 1) + a];
    const pts = [
      [i, j, i + 1, j],
      [i + 1, j, i + 1, j + 1],
      [i, j + 1, i + 1, j + 1],
      [i, j, i, j + 1],
    ][e];
    const va = v(pts[0], pts[1]);
    const vb = v(pts[2], pts[3]);
    const f = va / (va - vb);
    return [x0 + (pts[0] + (pts[2] - pts[0]) * f) * cell, y0 + (pts[1] + (pts[3] - pts[1]) * f) * cell];
  };
  const TABLE = [[], [[3, 2]], [[2, 1]], [[3, 1]], [[0, 1]], [[3, 0], [2, 1]], [[0, 2]], [[3, 0]], [[3, 0]], [[0, 2]], [[3, 2], [0, 1]], [[0, 1]], [[3, 1]], [[2, 1]], [[3, 2]], []];
  for (let j = 0; j < rows; j += 1)
    for (let i = 0; i < cols; i += 1) {
      const cx = x0 + (i + 0.5) * cell;
      const cy = y0 + (j + 0.5) * cell;
      if (!inCerebrum(cx, cy)) continue;
      const v = (a, b) => values[b * (cols + 1) + a] > 0;
      const index = (v(i, j) ? 8 : 0) | (v(i + 1, j) ? 4 : 0) | (v(i + 1, j + 1) ? 2 : 0) | (v(i, j + 1) ? 1 : 0);
      for (const [ea, eb] of TABLE[index]) {
        const [ax, ay] = edge(i, j, ea);
        const [bx, by] = edge(i, j, eb);
        segs.push(ax, ay, bx, by);
      }
    }
  // Each fold dissolves at its own moment when the brain breaks apart, in soft patches.
  const order = new Float32Array(segs.length / 4);
  for (let n = 0; n < order.length; n += 1) {
    const mx = (segs[n * 4] + segs[n * 4 + 2]) / 2;
    const my = (segs[n * 4 + 1] + segs[n * 4 + 3]) / 2;
    order[n] = 0.5 + 0.25 * Math.sin(mx * 4.3 + my * 1.7) + 0.25 * Math.cos(my * 5.1 - mx * 2.2);
  }
  const full = typeof Path2D === "undefined" ? null : new Path2D();
  for (let n = 0; n < segs.length; n += 4) {
    full?.moveTo(segs[n], segs[n + 1]);
    full?.lineTo(segs[n + 2], segs[n + 3]);
  }
  return { segs, order, full };
}

const path = (points, closed) => {
  const p = new Path2D();
  points.forEach(([x, y], i) => (i ? p.lineTo(x, y) : p.moveTo(x, y)));
  if (closed) p.closePath();
  return p;
};
const CEREBRUM_PATH = typeof Path2D === "undefined" ? null : path(CEREBRUM, true);
const SULCI = [
  // The lateral (Sylvian) fissure and the central sulcus, the two great folds
  [[-0.6, 0.22], [-0.3, 0.1], [0.05, 0.06], [0.32, -0.04], [0.52, -0.14]],
  [[0.08, -0.99], [0.1, -0.7], [0.0, -0.42], [0.04, -0.18], [-0.04, 0.07]],
  [[0.52, -0.84], [0.56, -0.6], [0.48, -0.4]],
];

// Draws the brain centred on (x, y). reveal runs from 1 (whole) to 0 (unravelled).
export function drawBrain(g, x, y, r, t, gyri, reveal = 1) {
  const px = 1 / r;
  g.save();
  g.translate(x, y);
  g.scale(r, r);
  g.lineCap = "round";
  g.lineJoin = "round";
  // A faint aura, pulsing outward
  for (let k = 0; k < 2; k += 1) {
    const q = ((t / 2600 + k * 0.5) % 1 + 1) % 1;
    g.save();
    g.scale(1 + q * 0.12, 1 + q * 0.12);
    g.strokeStyle = `rgba(25,25,25,${0.12 * (1 - q) * reveal})`;
    g.lineWidth = px;
    g.stroke(CEREBRUM_PATH);
    g.restore();
  }
  // Brain stem, then the cerebellum with its folia
  const stem = new Path2D();
  stem.moveTo(0.3, 0.46);
  stem.quadraticCurveTo(0.38, 0.78, 0.36, 1.02);
  stem.lineTo(0.55, 1.02);
  stem.quadraticCurveTo(0.54, 0.74, 0.6, 0.48);
  stem.closePath();
  g.fillStyle = "#f2f2f2";
  g.fill(stem);
  g.strokeStyle = "rgba(25,25,25,0.7)";
  g.lineWidth = 1.3 * px;
  g.stroke(stem);
  const { x: ex, y: ey, rx, ry, rot } = CEREBELLUM;
  const cb = new Path2D();
  cb.ellipse(ex, ey, rx, ry, rot, 0, Math.PI * 2);
  const shade = g.createLinearGradient(ex, ey - ry, ex, ey + ry);
  shade.addColorStop(0, "#ffffff");
  shade.addColorStop(1, "#e4e4e4");
  g.fillStyle = shade;
  g.fill(cb);
  g.save();
  g.clip(cb);
  g.strokeStyle = "rgba(25,25,25,0.34)";
  g.lineWidth = px;
  for (let k = 1; k < 10; k += 1) {
    const yy = ey - ry + (k * 2 * ry) / 10;
    g.beginPath();
    g.moveTo(ex - rx, yy + 0.04);
    g.quadraticCurveTo(ex, yy - 0.05 + Math.sin(k * 1.7) * 0.02, ex + rx, yy + 0.02);
    g.stroke();
  }
  g.restore();
  g.strokeStyle = "rgba(25,25,25,0.75)";
  g.lineWidth = 1.5 * px;
  g.stroke(cb);
  // The cerebrum: lit from the top left, shadowed underneath
  const light = g.createLinearGradient(-1.1, -1, 0.9, 0.6);
  light.addColorStop(0, "#ffffff");
  light.addColorStop(1, "#ebebeb");
  g.fillStyle = light;
  g.fill(CEREBRUM_PATH);
  g.save();
  g.clip(CEREBRUM_PATH);
  let folds = gyri.full;
  if (reveal < 1) {
    folds = new Path2D();
    const { segs, order } = gyri;
    for (let n = 0; n < order.length; n += 1) {
      if (order[n] > reveal) continue;
      folds.moveTo(segs[n * 4], segs[n * 4 + 1]);
      folds.lineTo(segs[n * 4 + 2], segs[n * 4 + 3]);
    }
  }
  // Each fold is a groove: a soft shadow just below it, then the line itself
  g.save();
  g.translate(0.003, 0.007);
  g.strokeStyle = "rgba(25,25,25,0.11)";
  g.lineWidth = 3 * px;
  g.stroke(folds);
  g.restore();
  g.strokeStyle = "rgba(25,25,25,0.6)";
  g.lineWidth = 1.1 * px;
  g.stroke(folds);
  const glow = g.createRadialGradient(-0.45, -0.55, 0, -0.45, -0.55, 1.1);
  glow.addColorStop(0, "rgba(255,255,255,0.55)");
  glow.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = glow;
  g.fillRect(-1.3, -1.1, 2.6, 1.8);
  const under = g.createLinearGradient(0, 0.05, 0, 0.6);
  under.addColorStop(0, "rgba(25,25,25,0)");
  under.addColorStop(1, "rgba(25,25,25,0.1)");
  g.fillStyle = under;
  g.fillRect(-1.3, -0.2, 2.6, 0.9);
  g.restore();
  g.strokeStyle = `rgba(25,25,25,${0.75 * reveal})`;
  g.lineWidth = 2.2 * px;
  SULCI.forEach((pts) => {
    const fold = new Path2D();
    fold.moveTo(...pts[0]);
    for (let i = 1; i < pts.length - 1; i += 1)
      fold.quadraticCurveTo(pts[i][0], pts[i][1], (pts[i][0] + pts[i + 1][0]) / 2, (pts[i][1] + pts[i + 1][1]) / 2);
    fold.lineTo(...pts[pts.length - 1]);
    g.stroke(fold);
  });
  g.strokeStyle = "rgba(25,25,25,0.85)";
  g.lineWidth = 1.8 * px;
  g.stroke(CEREBRUM_PATH);
  g.restore();
}
